// React dependencies
import React, { useEffect, useRef, useState } from 'react';

// External dependencies
import PropTypes from 'prop-types';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal dependencies
import { IsEmpty } from '../../common/checks';
import { Trim } from '../../common/util';
import Colours from '../../styles/colours';
import MTextField, { TextFieldStyle } from './MTextField';
import MButton from './MButton';
import Icons from './Icons';

const useStyles = makeStyles({
  textFieldInput: {
    height:     '100%',
    padding:    '0.2rem 0.3rem', // adjust padding of textfield's input text
    display:    'flex',
    alignItems: 'center', // 👈 vertical centering
    boxSizing:  'border-box',
  },
});

const TagsInput = (props) => {
  const classes = useStyles();

  const textFieldRef = useRef(null);
  const [tags, setTags] = useState([]);
  const [textEntered, setTextEntered] = useState('');

  const placeHolderText = 'E.g. architecture';
  const pillHeight = '1.5rem';

  useEffect(() => {
    setTags(props.data || []);
  }, [props.data]);

  const addPrependDisplayChar = (text = '') => {
    try {
      let txtStr = String(text);
      if (!IsEmpty(props.prependDisplayChar) && !txtStr.startsWith(props.prependDisplayChar)) {
        txtStr = String(props.prependDisplayChar) + txtStr;
      }

      return txtStr;
    } catch (error) {
      toast.error(error.message);
    }
  };

  const removePrependDisplayChar = (text = '') => {
    try {
      const prependChar = props.prependDisplayChar;
      let txtStr = String(text);
      if (!IsEmpty(prependChar) && txtStr.startsWith(String(prependChar))) {
        txtStr = txtStr.replace(String(prependChar), '');
      }
      return txtStr;
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatAndSaveTextEntered = () => {
    let formattedText = Trim(String(textEntered), false, true).toLowerCase();
    formattedText = removePrependDisplayChar(formattedText);

    if (IsEmpty(formattedText) || tags.includes(formattedText)) {
      return;
    }

    tags.push(formattedText);
    setTextEntered('');
    props.onChange(tags);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Backspace' && textEntered === '') {
      e.preventDefault();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      formatAndSaveTextEntered();
    }
  };

  const onInputChange = (newText) => {
    setTextEntered(newText);
  };

  const onInputFocusLost = () => {
    if (!IsEmpty(textEntered)) {
      formatAndSaveTextEntered();
    }
  };

  const removeTag = (text) => {
    const copy = tags.filter((t) => t !== text);
    setTags(copy);
    props.onChange(copy);
  };

  const formatDisplayText = (text = '') => {
    try {
      if (IsEmpty(text)) {
        return '';
      }

      text = addPrependDisplayChar(text);
      return text;
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={`d-flex flex-column px-1 py-1 ${props.className}`}
        style={{
          width:        '100%',
          minHeight:    '10rem',
          maxHeight:    '10rem',
          overflowY:    'auto',
          borderColor:  Colours.Gray2,
          borderWidth:  1.2,
          borderStyle:  'solid',
          borderRadius: TextFieldStyle.border.radius,
        }}
        onClick={() => (props.disabled ? {} : textFieldRef.current.focus())}
      >
        <div className="d-flex flex-row flex-wrap">
          {
            _.map(tags, (t) => (
              <MButton
                key={t}
                className={`pl-2 ${props.disabled ? 'pr-2' : 'pr-0'} py-0 mr-1 mb-1`}
                text={formatDisplayText(t)}
                textStyle={{
                  paddingTop:    '0.2rem',
                  paddingBottom: '0.2rem',
                  overflowWrap:  'break-word',
                  lineHeight:    '0.7rem',
                  color:         Colours.Gray,
                }}
                textAlign="left"
                textSize={TextFieldStyle.text.size}
                style={{
                  minHeight:       pillHeight,
                  borderRadius:    15,
                  backgroundColor: Colours.White,
                  borderColor:     Colours.LightGray2,
                  borderWidth:     1,
                  borderStyle:     'solid',
                  display:         'flex',
                  alignItems:      'center',
                  justifyContent:  'space-between',
                  paddingRight:    '0.3rem',
                }}
                disableElevation
                disabled={props.disabled}
                onClick={() => {}}
                endIcon={props.disabled ? undefined : () => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div
                    className="d-flex flex-column justify-content-center"
                    style={{
                      height:       pillHeight,
                      paddingRight: '0.8rem',
                    }}
                    onClick={() => (props.disabled ? {} : removeTag(t))}
                  >
                    <Icons.Cross fontSize={10} fill={TextFieldStyle.clearBtn.colour} />
                  </div>
                )}
              />
            ))
          }

          <MTextField
            ref={textFieldRef}
            value={textEntered}
            placeholder={!props.disabled && IsEmpty(tags) ? placeHolderText : ''}
            onBlur={onInputFocusLost}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            multiLine={false}
            // Note on 'enterKeyHint':
            // This is required in android to detect 'enter' key hit. We are not using multiLine
            // here, so the default behaviour is to go to the next text field input, so having
            // enterKeyHint='enter' allows us to modify that behaviour to trigger the enter key instead.
            enterKeyHint="enter"
            hideBorder
            style={{
            // make the width grow as we type
              width:
              (IsEmpty(tags) && IsEmpty(textEntered))
                ? `${placeHolderText.length * TextFieldStyle.text.size}px`
                : `${Math.max(6 * TextFieldStyle.text.size, String(textEntered).length * TextFieldStyle.text.size + 2)}px`,
              height: pillHeight,
            }}
            textColour={Colours.Gray}
            showClearButton={false}
            inputClass={classes.textFieldInput}
            disabled={props.disabled}
          />
        </div>
      </div>
    </>
  );
};

TagsInput.propTypes = {
  className:          PropTypes.string,
  data:               PropTypes.arrayOf(PropTypes.string),
  disabled:           PropTypes.bool,
  prependDisplayChar: PropTypes.string,
  onChange:           PropTypes.func.isRequired,
};

TagsInput.defaultProps = {
  className:          '',
  data:               [],
  disabled:           false,
  prependDisplayChar: '#',
};

export default TagsInput;
